import merge from 'lodash/merge';
import snakeCase from 'lodash/snakeCase';
import camelCase from 'lodash/camelCase';
import get from 'lodash/get';
import isUndefined from 'lodash/isUndefined';
import forOwn from 'lodash/forOwn';
import { Inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { formatData } from '../helpers/data-helper';
import { HttpHeaders, HttpParams } from '@angular/common/http';

interface Api {
  host: string;
}

@Injectable()
export class ApiService {
  defaultOptions: { [key: string]: any };

  constructor(private _http: HttpClient,
              @Inject('AppConfig') private _appConfig) {

    if (!this._appConfig) {
      throw new Error('Api config is not defined');
    } else {
      this.defaultOptions = this._appConfig.api.options;
    }
  }

  /**
   * whatever domain/feature method name
   */
  get(apiKey: string, pathKey: string, urlParams?: any, options?: any): Observable<any> {
    const url = this.buildUrl(apiKey, pathKey, urlParams);

    if (!url) {
      return throwError(`url is not found please check keys, ${apiKey}, ${pathKey}`);
    }
    return this.handleResponse(this._http.get(url, this.getOptions(options)));
  }

  post(apiKey: string, pathKey: string, urlParams?: any, body?: any, options?: any): Observable<any> {
    return this._payloadRequest('post', apiKey, pathKey, urlParams, body, options);
  }

  patch(apiKey: string, pathKey: string, urlParams?: any, body?: any, options?: any): Observable<any> {
    return this._payloadRequest('patch', apiKey, pathKey, urlParams, body, options);
  }

  put(apiKey: string, pathKey: string, urlParams?: any, body?: any, options?: any): Observable<any> {
    return this._payloadRequest('put', apiKey, pathKey, urlParams, body, options);
  }

  remove(apiKey: string, pathKey: string, urlParams?: any, options?: any) {
    const url = this.buildUrl(apiKey, pathKey, urlParams);

    if (!url) {
      return throwError(`url is not found please check keys, ${apiKey}, ${pathKey}`);
    }

    return this.handleResponse(this._http.delete(url, this.getOptions(options)));
  }

  downloadFile(apiKey: string,
               pathKey: string,
               urlParams?: any,
               downloadAsName?: string,
               options?: { params?: HttpParams, headers?: HttpHeaders },
               withCredentials = true) {
    const url = this.buildUrl(apiKey, pathKey, urlParams);
    return firstValueFrom(this._http.get(url, {
      params: options ? options.params : {},
      headers: options ? options.headers : {},
      responseType: 'blob' as 'json',
      withCredentials
    }))
      .then((response: any) => {
        const downloadLink = document.createElement('a');
        const blob = new Blob([response], { type: response.type });
        const url = window.URL.createObjectURL(blob);
        downloadLink.href = url;

        if (downloadAsName) {
          downloadLink.download = downloadAsName;
        }

        document.body.appendChild(downloadLink);
        downloadLink.click();

        window.URL.revokeObjectURL(url);
      });
  }

  _payloadRequest(method: string, apiKey: string,
                  pathKey: string, urlParams?: any, body?: any, options?: any) {
    const url = this.buildUrl(apiKey, pathKey, urlParams);
    if (!url) {
      return throwError(`url is not found please check keys, ${apiKey}, ${pathKey}`);
    }

    return this
      .handleResponse(this._http[method](url,
        this.snakePayload(body) || {}, this.getOptions(options)));
  }

  getOptions(options: any = {}) {
    let requestOptions = this.defaultOptions;
    if (options) {
      requestOptions = merge({}, this.defaultOptions, options);
    }

    if (requestOptions.params) {
      const snakedParams = {};
      forOwn(requestOptions.params, (value, key) => {
        if (key && value) {
          snakedParams[snakeCase(key)] = value;
        }
      });
      requestOptions.params = snakedParams;
    }

    return requestOptions;
  }

  camelResponse(response) {
    if (response.controls === Array) {
      return response.map((i) => formatData(i, camelCase));
    }

    return formatData(response, camelCase);
  }

  snakePayload(data) {
    if (!data) {
      return null;
    }

    if (data.constructor === Array) {
      return data.map((item) => this.snakePayload(item));
    }

    if (['number', 'string', 'boolean'].indexOf(typeof data) >= 0) {
      return data;
    }

    return formatData(Object.assign({}, data), snakeCase);
  }

  handleResponse(obs: Observable<any>) {
    return obs
      .pipe(map((res) => {
        if (res) {
          return this.camelResponse(res);
        }
        return res;
      }))
      .pipe(catchError((err: HttpErrorResponse) => {
        if (err.error
          && err.headers.get('content-type')
          && err.headers.get('content-type').includes('application/json')) {
          return throwError({ ...err, error: this.camelResponse(err.error) });
        }

        return throwError(err);
      }));
  }

  buildUrl(apiKey: string, pathKey: string, urlParams = {}) {
    if (apiKey.indexOf('api.') !== 0) {
      apiKey = 'api.' + apiKey;
    }

    const api = <Api>get(this._appConfig, apiKey);
    const path = <string>get(api, pathKey);

    if (isUndefined(api) || isUndefined(path)) {
      console
        .error(`apiKey: '${apiKey}' or pathKey '${pathKey}' were not found in config, ${JSON.stringify(this._appConfig)}`);
      return '';
    }

    let url = api.host + this._fillUrl(path, urlParams);
    if (url.slice(-1) !== '/') {
      url += '/';
    }

    return url;
  }

  _fillUrl(url: string, params = {}) {
    forOwn(params, (value, key) => {
      url = url.replace(new RegExp(':' + key), <string>value);
    });

    return url;
  }
}
